import {
  getCardCenterX,
  getMainCenterInRowCoords,
  WORKFLOW_MAIN_CARD_WIDTH,
  WORKFLOW_MAIN_COLUMN_WIDTH,
} from './workflowCardAnchors';
import { MIN_COMFORTABLE_CANVAS_WIDTH } from './workflowBuilderLayout';

export function getLayoutWidthFromElement(element) {
  if (!element) return 0;
  const layoutWidth = element.offsetWidth || element.clientWidth || 0;
  if (layoutWidth > 0) {
    return layoutWidth;
  }
  return element.getBoundingClientRect?.().width ?? 0;
}

export function getLayoutScaleFactor(element) {
  if (!element) return 1;
  const layoutWidth = getLayoutWidthFromElement(element);
  const rect = element.getBoundingClientRect?.();
  if (!layoutWidth || !rect?.width) return 1;
  return layoutWidth / rect.width;
}

export function getElementCenterX(element, containerRect, containerElement = null) {
  if (!element || !containerRect) return 0;

  const rect = element.getBoundingClientRect();
  const screenCenterX = rect.left - containerRect.left + rect.width / 2;
  const scale = containerElement ? getLayoutScaleFactor(containerElement) : 1;
  return screenCenterX * scale;
}

export function getElementBottomY(element, containerRect, containerElement = null) {
  if (!element || !containerRect) return 0;

  const rect = element.getBoundingClientRect();
  const screenBottomY = rect.bottom - containerRect.top;
  const scale = containerElement ? getLayoutScaleFactor(containerElement) : 1;
  return screenBottomY * scale;
}

export function getStableMainLaneCenterX(
  rowWidth,
  { hasRealTriggers, addTileCenterX, mainLaneColumnWidth = WORKFLOW_MAIN_COLUMN_WIDTH } = {},
) {
  if (!hasRealTriggers) {
    return addTileCenterX ?? getCardCenterX(mainLaneColumnWidth);
  }

  return getCardCenterX(mainLaneColumnWidth);
}

export function getMainLaneCenterInRowCoords(
  rowWidth,
  { mainLaneColumnWidth = WORKFLOW_MAIN_COLUMN_WIDTH } = {},
) {
  return getMainCenterInRowCoords(rowWidth ?? mainLaneColumnWidth);
}

/** Center X of real trigger cards only (add-tile is excluded from trunk alignment). */
export function getTriggerGroupCenterX(triggerCenters, fallbackX = null) {
  if (!triggerCenters?.length) {
    return fallbackX;
  }
  if (triggerCenters.length === 1) {
    return triggerCenters[0];
  }
  return (Math.min(...triggerCenters) + Math.max(...triggerCenters)) / 2;
}

/** Center X of the full visible top row (triggers + add tile). */
export function getVisibleRowCenterX(itemCenters, fallbackX = null) {
  if (!itemCenters?.length) {
    return fallbackX;
  }
  if (itemCenters.length === 1) {
    return itemCenters[0];
  }
  return (Math.min(...itemCenters) + Math.max(...itemCenters)) / 2;
}

/**
 * Derive stable main-chain action lane geometry.
 * Local card/connector geometry stays fixed at 340 / 170.
 * Global lane position follows triggerMergeCenterX from the visible trigger row.
 */
export function getWorkflowActionLaneLayout(
  connectorLayout,
  { mainLaneColumnWidth = WORKFLOW_MAIN_COLUMN_WIDTH } = {},
) {
  const localCenterX = getCardCenterX(mainLaneColumnWidth);

  if (!connectorLayout) {
    return {
      centerX: localCenterX,
      globalCenterX: localCenterX,
      width: mainLaneColumnWidth,
      wrapperWidth: mainLaneColumnWidth,
      offset: 0,
      mergeCenterX: localCenterX,
      actionLaneCenterX: localCenterX,
      triggerRowVisualCenterX: localCenterX,
      triggerRowVisualWidth: mainLaneColumnWidth,
      triggerRowWidth: mainLaneColumnWidth,
      mainColumnWidth: mainLaneColumnWidth,
    };
  }

  const mergeCenterX = connectorLayout.triggerMergeCenterX
    ?? connectorLayout.triggerRowVisualCenterX
    ?? connectorLayout.connectorCenterX
    ?? localCenterX;
  const triggerRowWidth = connectorLayout.visualRowWidth
    ?? connectorLayout.rowWidth
    ?? mainLaneColumnWidth;
  const mainChainLeft = mergeCenterX - localCenterX;

  return {
    centerX: localCenterX,
    globalCenterX: mergeCenterX,
    width: mainLaneColumnWidth,
    wrapperWidth: Math.max(mainLaneColumnWidth, triggerRowWidth),
    offset: mainChainLeft,
    mergeCenterX,
    actionLaneCenterX: localCenterX,
    triggerRowVisualCenterX: connectorLayout.triggerRowVisualCenterX ?? mergeCenterX,
    triggerRowVisualWidth: triggerRowWidth,
    triggerRowWidth,
    mainColumnWidth: mainLaneColumnWidth,
  };
}

/** Returns true when trigger-row connector geometry is unchanged. */
export function isSameTriggerConnectorGeometry(previous, next) {
  if (previous === next) return true;
  if (!previous || !next) return false;

  return previous.visualRowWidth === next.visualRowWidth
    && previous.rowWidth === next.rowWidth
    && previous.connectorRowOffset === next.connectorRowOffset
    && previous.triggerMergeCenterX === next.triggerMergeCenterX
    && previous.triggerRowVisualCenterX === next.triggerRowVisualCenterX
    && previous.actionLaneCenterX === next.actionLaneCenterX
    && previous.includeAddTileConnector === next.includeAddTileConnector
    && previous.hasMerge === next.hasMerge
    && previous.triggerCenters?.length === next.triggerCenters?.length
    && (previous.triggerCenters || []).every((value, index) => value === next.triggerCenters[index])
    && (previous.branchCenters || []).every((value, index) => value === next.branchCenters[index]);
}

function getLaneCenterInRowCoords(laneColumnElement, rowRect, mainLaneColumnWidth = WORKFLOW_MAIN_COLUMN_WIDTH, rowElement = null) {
  if (!laneColumnElement || !rowRect) {
    const rowWidth = rowElement ? getLayoutWidthFromElement(rowElement) : rowRect?.width ?? mainLaneColumnWidth;
    return getMainLaneCenterInRowCoords(rowWidth, { mainLaneColumnWidth });
  }

  return getElementCenterX(laneColumnElement, rowRect, rowElement);
}

function getConnectorRowOffset(rowRect, laneColumnElement) {
  if (!rowRect || !laneColumnElement) {
    return 0;
  }

  const laneColumnRect = laneColumnElement.getBoundingClientRect();
  return rowRect.left - laneColumnRect.left;
}

function buildBranchMergeLayout({
  sourceType,
  triggerCenters,
  addTileCenterX,
  branchCenters,
  triggerBottoms,
  addTileBottomY,
  mainLaneCenterX,
  mainLaneCenterXRow,
  connectorRowWidth,
  connectorRowOffset,
  visualRowWidth,
  mainLaneColumnWidth,
  includeAddTileConnector,
}) {
  const visibleRowCenterX = getVisibleRowCenterX(branchCenters, mainLaneCenterXRow);
  const trunkEndRowX = getMainLaneCenterInRowCoords(connectorRowWidth, { mainLaneColumnWidth });
  const sourceBottomY = Math.max(
    ...triggerBottoms,
    addTileBottomY || 0,
  );

  return {
    sourceType,
    hasRealTriggers: true,
    triggerCenters,
    addTileCenterX,
    branchCenters,
    triggerRowVisualCenterX: visibleRowCenterX,
    triggerMergeCenterX: visibleRowCenterX,
    triggerTrunkEndX: visibleRowCenterX,
    actionLaneCenterX: mainLaneCenterX,
    trunkEndRowX,
    mainLaneCenterX,
    mainLaneCenterXRow,
    connectorCenterX: visibleRowCenterX,
    sourceBottomY,
    includeAddTileConnector,
    hasMerge: branchCenters.length > 1,
    rowWidth: connectorRowWidth,
    visualRowWidth,
    connectorRowOffset,
    mainLaneColumnWidth,
  };
}

export function buildMeasuredTriggerConnectorLayout({
  rowElement,
  laneColumnElement = null,
  triggerElements = [],
  addTileElement = null,
  expectAddTile = false,
  mainLaneColumnWidth = WORKFLOW_MAIN_COLUMN_WIDTH,
}) {
  if (!rowElement) {
    return null;
  }

  const rowRect = rowElement.getBoundingClientRect();
  const layoutRowWidth = getLayoutWidthFromElement(rowElement);
  if (!layoutRowWidth) {
    return null;
  }

  const validTriggerElements = triggerElements.filter(Boolean);
  const triggerCenters = validTriggerElements.map((element) =>
    getElementCenterX(element, rowRect, rowElement),
  );

  const triggerBottoms = validTriggerElements.map((element) =>
    getElementBottomY(element, rowRect, rowElement),
  );

  const addTileCenterX = addTileElement
    ? getElementCenterX(addTileElement, rowRect, rowElement)
    : null;

  const addTileBottomY = addTileElement
    ? getElementBottomY(addTileElement, rowRect, rowElement)
    : null;

  const hasAddTile = Number.isFinite(addTileCenterX);
  const hasRealTriggers = triggerCenters.length > 0;
  const mainLaneCenterX = getStableMainLaneCenterX(layoutRowWidth, {
    hasRealTriggers,
    addTileCenterX,
    mainLaneColumnWidth,
  });
  const mainLaneCenterXRow = getLaneCenterInRowCoords(laneColumnElement, rowRect, mainLaneColumnWidth, rowElement);
  const connectorRowOffset = getConnectorRowOffset(rowRect, laneColumnElement);
  const visualRowWidth = layoutRowWidth;
  const connectorRowWidth = Math.max(mainLaneColumnWidth, visualRowWidth);

  if (!hasRealTriggers) {
    if (!hasAddTile) {
      return null;
    }

    return {
      sourceType: 'add-tile',
      hasRealTriggers: false,
      triggerCenters: [],
      addTileCenterX,
      branchCenters: [addTileCenterX],
      triggerRowVisualCenterX: addTileCenterX,
      triggerMergeCenterX: addTileCenterX,
      triggerTrunkEndX: addTileCenterX,
      actionLaneCenterX: mainLaneCenterX,
      trunkEndRowX: getMainLaneCenterInRowCoords(connectorRowWidth, { mainLaneColumnWidth }),
      mainLaneCenterX,
      mainLaneCenterXRow: addTileCenterX,
      connectorCenterX: addTileCenterX,
      sourceBottomY: addTileBottomY || 0,
      includeAddTileConnector: true,
      hasMerge: false,
      rowWidth: connectorRowWidth,
      visualRowWidth,
      connectorRowOffset: 0,
      mainLaneColumnWidth,
    };
  }

  if (expectAddTile && !hasAddTile) {
    return null;
  }

  if (hasAddTile) {
    const branchCenters = [...triggerCenters, addTileCenterX];
    return buildBranchMergeLayout({
      sourceType: triggerCenters.length === 1 ? 'single-trigger-with-add' : 'multi-trigger-with-add',
      triggerCenters,
      addTileCenterX,
      branchCenters,
      triggerBottoms,
      addTileBottomY,
      mainLaneCenterX,
      mainLaneCenterXRow,
      connectorRowWidth,
      connectorRowOffset,
      visualRowWidth,
      mainLaneColumnWidth,
      includeAddTileConnector: true,
    });
  }

  const branchCenters = [...triggerCenters];

  if (triggerCenters.length === 1) {
    const triggerCenterX = triggerCenters[0];

    return {
      sourceType: 'single-trigger',
      hasRealTriggers: true,
      triggerCenters: [triggerCenterX],
      addTileCenterX,
      branchCenters,
      triggerRowVisualCenterX: triggerCenterX,
      triggerMergeCenterX: triggerCenterX,
      triggerTrunkEndX: triggerCenterX,
      actionLaneCenterX: mainLaneCenterX,
      trunkEndRowX: getMainLaneCenterInRowCoords(connectorRowWidth, { mainLaneColumnWidth }),
      mainLaneCenterX,
      mainLaneCenterXRow,
      connectorCenterX: triggerCenterX,
      sourceBottomY: triggerBottoms[0] || 0,
      includeAddTileConnector: false,
      hasMerge: false,
      rowWidth: connectorRowWidth,
      visualRowWidth,
      connectorRowOffset,
      mainLaneColumnWidth,
    };
  }

  return buildBranchMergeLayout({
    sourceType: 'multi-trigger',
    triggerCenters,
    addTileCenterX,
    branchCenters,
    triggerBottoms,
    addTileBottomY: 0,
    mainLaneCenterX,
    mainLaneCenterXRow,
    connectorRowWidth,
    connectorRowOffset,
    visualRowWidth,
    mainLaneColumnWidth,
    includeAddTileConnector: false,
  });
}

/** @deprecated Use buildMeasuredTriggerConnectorLayout */
export function buildTriggerConnectorLayout({
  triggerRowElement,
  triggerElements = [],
  addTileElement = null,
}) {
  return buildMeasuredTriggerConnectorLayout({
    rowElement: triggerRowElement,
    triggerElements,
    addTileElement,
  });
}

export function resolveTriggerConnectorLayout({
  triggerRowElement,
  rowElement,
  laneColumnElement = null,
  triggerElements = [],
  addTileElement = null,
  triggerCount = 0,
  expectAddTile = false,
}) {
  const measured = buildMeasuredTriggerConnectorLayout({
    rowElement: rowElement || triggerRowElement,
    laneColumnElement,
    triggerElements,
    addTileElement,
    expectAddTile,
  });

  if (measured) {
    return measured;
  }

  if (triggerCount > 0) {
    return null;
  }

  return buildMeasuredTriggerConnectorLayout({
    rowElement: rowElement || triggerRowElement,
    laneColumnElement,
    triggerElements: [],
    addTileElement,
  });
}

/** Test helper for deterministic geometry without DOM measurement. */
export function buildDeterministicTriggerConnectorLayout(
  triggerCount,
  { cardWidth = 340, gap = 16, includeAddTileSlot = false } = {},
) {
  const mainLaneCenterX = cardWidth / 2;

  if (triggerCount <= 0) {
    return {
      sourceType: 'add-tile',
      hasRealTriggers: false,
      triggerCenters: [],
      addTileCenterX: mainLaneCenterX,
      branchCenters: [mainLaneCenterX],
      triggerRowVisualCenterX: mainLaneCenterX,
      triggerMergeCenterX: mainLaneCenterX,
      triggerTrunkEndX: mainLaneCenterX,
      actionLaneCenterX: mainLaneCenterX,
      trunkEndRowX: mainLaneCenterX,
      mainLaneCenterX,
      mainLaneCenterXRow: mainLaneCenterX,
      connectorCenterX: mainLaneCenterX,
      sourceBottomY: 132,
      includeAddTileConnector: true,
      hasMerge: false,
      rowWidth: cardWidth,
      visualRowWidth: cardWidth,
      connectorRowOffset: 0,
      mainLaneColumnWidth: cardWidth,
    };
  }

  const triggerGroupWidth = triggerCount * cardWidth + (triggerCount - 1) * gap;
  const visualRowWidth = includeAddTileSlot
    ? triggerGroupWidth + gap + cardWidth
    : triggerGroupWidth;
  const columnOffset = includeAddTileSlot
    ? Math.max(0, (visualRowWidth - cardWidth) / 2)
    : 0;
  const rowStartOffset = mainLaneCenterX - visualRowWidth / 2;
  const triggerCenters = Array.from({ length: triggerCount }, (_, index) => (
    rowStartOffset + index * (cardWidth + gap) + cardWidth / 2
  ));
  const addTileCenterX = includeAddTileSlot
    ? rowStartOffset + triggerGroupWidth + gap + cardWidth / 2
    : null;
  const hasAddTile = Number.isFinite(addTileCenterX);

  if (hasAddTile) {
    const branchCenters = [...triggerCenters, addTileCenterX];
    const visibleRowCenterX = getVisibleRowCenterX(branchCenters, mainLaneCenterX);
    const trunkEndRowX = getMainLaneCenterInRowCoords(visualRowWidth, { mainLaneColumnWidth: cardWidth });

    return {
      sourceType: triggerCount === 1 ? 'single-trigger-with-add' : 'multi-trigger-with-add',
      hasRealTriggers: true,
      triggerCenters,
      addTileCenterX,
      branchCenters,
      triggerRowVisualCenterX: visibleRowCenterX,
      triggerMergeCenterX: visibleRowCenterX,
      triggerTrunkEndX: visibleRowCenterX,
      actionLaneCenterX: mainLaneCenterX,
      trunkEndRowX,
      mainLaneCenterX,
      mainLaneCenterXRow: columnOffset + mainLaneCenterX,
      connectorCenterX: visibleRowCenterX,
      sourceBottomY: 132,
      includeAddTileConnector: true,
      hasMerge: true,
      rowWidth: Math.max(cardWidth, visualRowWidth),
      visualRowWidth,
      connectorRowOffset: -columnOffset,
      mainLaneColumnWidth: cardWidth,
    };
  }

  const visibleRowCenterX = getVisibleRowCenterX(triggerCenters, mainLaneCenterX);
  const trunkEndRowX = getMainLaneCenterInRowCoords(
    triggerCount === 1 ? cardWidth : visualRowWidth,
    { mainLaneColumnWidth: cardWidth },
  );

  return {
    sourceType: triggerCount === 1 ? 'single-trigger' : 'multi-trigger',
    hasRealTriggers: true,
    triggerCenters,
    addTileCenterX,
    branchCenters: [...triggerCenters],
    triggerRowVisualCenterX: visibleRowCenterX,
    triggerMergeCenterX: visibleRowCenterX,
    triggerTrunkEndX: visibleRowCenterX,
    actionLaneCenterX: mainLaneCenterX,
    trunkEndRowX,
    mainLaneCenterX,
    mainLaneCenterXRow: mainLaneCenterX,
    connectorCenterX: visibleRowCenterX,
    sourceBottomY: 132,
    includeAddTileConnector: false,
    hasMerge: triggerCount > 1,
    rowWidth: Math.max(cardWidth, triggerCount === 1 ? cardWidth : visualRowWidth),
    visualRowWidth,
    connectorRowOffset: 0,
    mainLaneColumnWidth: cardWidth,
  };
}

/** Proof-page / schema canvas dimensions (content-relative coordinates). */
export const WORKFLOW_PROOF_DIMENSIONS = {
  triggerCardWidth: 340,
  triggerCardHeight: 132,
  addTileWidth: 340,
  addTileHeight: 132,
  actionWidth: 340,
  actionHeight: 88,
  conditionWidth: 340,
  conditionHeight: 88,
  branchWidth: 280,
  branchHeight: 96,
  plusSize: 28,
  endWidth: 120,
  endHeight: 32,
  gap: 16,
  branchGap: 16,
  mergeZoneHeight: 52,
  stepConnectorHeight: 36,
  forkStemHeight: 32,
  forkDropHeight: 72,
  branchLaneDrop: 28,
  branchRailGapAboveCard: 32,
  branchConnectorDrop: 32,
  branchCornerRadius: 14,
  conditionCenterRadius: 14,
  defaultPaddingX: 48,
  defaultPaddingY: 48,
  triggerConnectorDrop: 36,
  triggerMergeGap: 36,
  triggerCornerRadius: 14,
  triggerCenterRadius: 14,
  trunkToPlusGap: 28,
  plusToNextGap: 28,
};

export function createWorkflowNode(id, type, x, y, width, height, meta = {}) {
  return {
    id,
    type,
    x,
    y,
    width,
    height,
    centerX: x + width / 2,
    topY: y,
    bottomY: y + height,
    leftX: x,
    rightX: x + width,
    ...meta,
  };
}

export function computeTriggerRowGeometry({
  triggerCount,
  triggerCardWidth,
  addTileWidth,
  gap,
  contentWidth,
  includeAddTile = true,
}) {
  const triggerGroupWidth = triggerCount === 0
    ? 0
    : triggerCount * triggerCardWidth + (triggerCount - 1) * gap;
  const fullTriggerRowWidth = triggerCount === 0
    ? addTileWidth
    : triggerGroupWidth + gap + addTileWidth;
  const rowStartX = (contentWidth - fullTriggerRowWidth) / 2;

  const triggerSlots = Array.from({ length: triggerCount }, (_, index) => {
    const x = rowStartX + index * (triggerCardWidth + gap);
    return {
      x,
      centerX: x + triggerCardWidth / 2,
    };
  });

  const addTileX = triggerCount === 0
    ? rowStartX
    : rowStartX + triggerGroupWidth + gap;
  const addTileCenterX = addTileX + addTileWidth / 2;
  const triggerCenters = triggerSlots.map((slot) => slot.centerX);
  const itemCenters = includeAddTile
    ? [...triggerCenters, addTileCenterX]
    : [...triggerCenters];
  const visibleRowCenterX = getVisibleRowCenterX(
    itemCenters.length > 0 ? itemCenters : [addTileCenterX],
  );
  const actionLaneCenterX = rowStartX + getMainCenterInRowCoords(fullTriggerRowWidth);

  return {
    triggerGroupWidth,
    fullTriggerRowWidth,
    rowStartX,
    triggerCenters,
    addTileCenterX,
    itemCenters,
    visibleRowCenterX,
    triggerRowVisualCenterX: visibleRowCenterX,
    actionLaneCenterX,
    triggerMergeCenterX: visibleRowCenterX,
    triggerSlots,
    addTileX,
  };
}

export function computeBranchRowGeometry({
  branchCount,
  branchCardWidth,
  branchGap,
  conditionCenterX,
  contentWidth,
  paddingX,
}) {
  const branchRowWidth = branchCount * branchCardWidth + (branchCount - 1) * branchGap;
  let branchRowStartX = conditionCenterX - branchRowWidth / 2;
  const minStart = paddingX;
  const maxStart = contentWidth - paddingX - branchRowWidth;

  if (branchRowStartX < minStart) {
    branchRowStartX = minStart;
  } else if (branchRowStartX > maxStart) {
    branchRowStartX = Math.max(minStart, (contentWidth - branchRowWidth) / 2);
  }

  const branchSlots = Array.from({ length: branchCount }, (_, index) => {
    const x = branchRowStartX + index * (branchCardWidth + branchGap);
    return {
      x,
      centerX: x + branchCardWidth / 2,
    };
  });

  return {
    branchRowWidth,
    branchRowStartX,
    branchCenters: branchSlots.map((slot) => slot.centerX),
    branchSlots,
  };
}

function resolveProofScenarioFlags(scenario) {
  if (scenario === 'stable-drawer-open-behavior') {
    return {
      hasAction: true,
      hasCondition: false,
      hasFullFlowPlus: true,
      hasBranches: false,
      hasMainEnd: true,
      multiAction: true,
    };
  }

  if (scenario === 'stable-main-lane-condition-toggle'
    || scenario === 'stable-ghl-ifelse-behavior') {
    return {
      hasAction: true,
      hasCondition: true,
      hasFullFlowPlus: true,
      hasBranches: true,
      hasMainEnd: false,
    };
  }

  return {
    hasAction: scenario === 'condition-below-action' || scenario === 'full-flow',
    hasCondition: scenario !== 'triggers',
    hasFullFlowPlus: scenario === 'full-flow'
      || scenario === 'stable-main-lane-condition-toggle'
      || scenario === 'stable-ghl-ifelse-behavior',
    hasBranches: scenario !== 'triggers',
    hasMainEnd: scenario === 'triggers',
  };
}

/**
 * Stable hash of proof node centers used to detect connector geometry drift.
 */
export function buildProofGeometryHash(schema) {
  if (!schema?.nodes?.length) return '';

  return schema.nodes
    .map((node) => `${node.id}:${node.centerX},${node.centerY}`)
    .join('|');
}

/**
 * Compare graph geometry with drawer closed vs open.
 * Drawer only changes visual viewport width, never graph coordinates.
 */
export function buildStableDrawerProofComparison({
  triggerCount = 3,
  actionCount = 3,
  branchCount = 2,
  canvasWidth = 1200,
  canvasHeight = 720,
  editorWidth = 480,
  longLabels = false,
  includeAddTile = true,
  scenario = 'stable-drawer-open-behavior',
  ...rest
} = {}) {
  const baseParams = {
    scenario,
    triggerCount,
    actionCount,
    branchCount,
    canvasWidth,
    canvasHeight,
    editorWidth,
    longLabels,
    includeAddTile,
    ...rest,
  };

  const drawerClosed = buildWorkflowProofCanvasSchema({
    ...baseParams,
    drawerOpen: false,
  });
  const drawerOpen = buildWorkflowProofCanvasSchema({
    ...baseParams,
    drawerOpen: true,
  });

  const snapshot = (schema) => ({
    mainLaneX: schema.actionLaneX,
    triggerMergeCenterX: schema.visibleRowCenterX,
    actionCenters: schema.nodes.filter((node) => node.type === 'action').map((node) => node.centerX),
    plusCenters: schema.nodes.filter((node) => node.type === 'plus').map((node) => node.centerX),
    graphRootWidth: schema.debug.graphRootWidth,
    visualViewportWidth: schema.debug.visualViewportWidth,
    connectorPathsHash: buildProofGeometryHash(schema),
  });

  const closed = snapshot(drawerClosed);
  const open = snapshot(drawerOpen);

  const didMainLaneMove = closed.mainLaneX !== open.mainLaneX
    || closed.triggerMergeCenterX !== open.triggerMergeCenterX
    || closed.actionCenters.join(',') !== open.actionCenters.join(',')
    || closed.plusCenters.join(',') !== open.plusCenters.join(',');

  return {
    drawerOpen: true,
    closed,
    open,
    mainLaneXBefore: closed.mainLaneX,
    mainLaneXAfter: open.mainLaneX,
    triggerMergeCenterBefore: closed.triggerMergeCenterX,
    triggerMergeCenterAfter: open.triggerMergeCenterX,
    connectorPathsHashBefore: closed.connectorPathsHash,
    connectorPathsHashAfter: open.connectorPathsHash,
    didMainLaneMove,
    didConnectorPathsChange: closed.connectorPathsHash !== open.connectorPathsHash,
    drawerClosed,
    drawerOpenSchema: drawerOpen,
  };
}

/**
 * Compare main-lane geometry with and without a condition step.
 * Used by the proof page and regression tests to ensure branches never move mainLaneX.
 */
export function buildStableMainLaneProofComparison({
  triggerCount = 1,
  branchCount = 2,
  canvasWidth = 1200,
  canvasHeight = 720,
  longLabels = false,
  includeAddTile = true,
  scenario = 'stable-main-lane-condition-toggle',
  ...rest
} = {}) {
  const baseParams = {
    scenario,
    triggerCount,
    branchCount,
    canvasWidth,
    canvasHeight,
    longLabels,
    includeAddTile,
    ...rest,
  };

  const withoutCondition = buildWorkflowProofCanvasSchema({
    ...baseParams,
    includeCondition: false,
  });
  const withCondition = buildWorkflowProofCanvasSchema({
    ...baseParams,
    includeCondition: true,
  });

  const snapshot = (schema) => ({
    mainLaneX: schema.actionLaneX,
    triggerMergeCenterX: schema.visibleRowCenterX,
    firstActionCenterX: schema.debug.firstActionCenterX,
    conditionCenterX: schema.conditionNode?.centerX ?? null,
    conditionParentCenterX: schema.conditionNode?.centerX ?? null,
    branchRowWidth: schema.debug.branches?.branchRowWidth ?? null,
    contentWidth: schema.content.width,
  });

  const before = snapshot(withoutCondition);
  const after = snapshot(withCondition);

  const didMainLaneMove = before.mainLaneX !== after.mainLaneX
    || before.triggerMergeCenterX !== after.triggerMergeCenterX
    || before.firstActionCenterX !== after.firstActionCenterX;

  return {
    before,
    after,
    mainLaneXBefore: before.mainLaneX,
    mainLaneXAfter: after.mainLaneX,
    triggerMergeCenterBefore: before.triggerMergeCenterX,
    triggerMergeCenterAfter: after.triggerMergeCenterX,
    actionCenterBefore: before.firstActionCenterX,
    actionCenterAfter: after.firstActionCenterX,
    conditionCenter: after.conditionCenterX,
    branchRowWidth: after.branchRowWidth,
    didMainLaneMove,
    withoutCondition,
    withCondition,
  };
}

/**
 * Build the full content-relative workflow canvas schema for the localhost proof page.
 */
export function buildWorkflowProofCanvasSchema({
  scenario = 'triggers',
  triggerCount = 0,
  branchCount = 2,
  canvasWidth = 1200,
  canvasHeight = 720,
  paddingX = WORKFLOW_PROOF_DIMENSIONS.defaultPaddingX,
  paddingY = WORKFLOW_PROOF_DIMENSIONS.defaultPaddingY,
  longLabels = false,
  includeAddTile = true,
  includeCondition = null,
  drawerOpen = false,
  editorWidth = 480,
  actionCount = null,
  dimensions = WORKFLOW_PROOF_DIMENSIONS,
} = {}) {
  const {
    triggerCardWidth,
    triggerCardHeight,
    addTileWidth,
    addTileHeight,
    actionWidth,
    actionHeight,
    conditionWidth,
    conditionHeight,
    branchWidth,
    branchHeight,
    plusSize,
    endWidth,
    endHeight,
    gap,
    branchGap,
    stepConnectorHeight,
    forkStemHeight,
    forkDropHeight,
    branchLaneDrop,
    branchRailGapAboveCard,
    branchConnectorDrop,
    branchCornerRadius,
    conditionCenterRadius,
    triggerConnectorDrop,
    triggerMergeGap,
    triggerCornerRadius,
    triggerCenterRadius,
    trunkToPlusGap,
    plusToNextGap,
  } = dimensions;

  const flags = resolveProofScenarioFlags(scenario);
  if (includeCondition !== null) {
    flags.hasCondition = includeCondition;
    flags.hasBranches = includeCondition;
    flags.hasMainEnd = !includeCondition
      && (scenario === 'stable-main-lane-condition-toggle' || scenario === 'stable-ghl-ifelse-behavior');
  }
  const laneBranchCount = flags.hasBranches ? branchCount : 0;
  const branchRowWidthEstimate = laneBranchCount > 0
    ? laneBranchCount * branchWidth + (laneBranchCount - 1) * branchGap
    : 0;

  const triggerGroupWidthEstimate = triggerCount === 0
    ? 0
    : triggerCount * triggerCardWidth + (triggerCount - 1) * gap;
  const fullTriggerRowWidthEstimate = triggerCount === 0
    ? addTileWidth
    : triggerGroupWidthEstimate + gap + addTileWidth;

  const canvasAvailableWidth = canvasWidth - paddingX * 2;
  const mainLaneContentWidth = Math.max(
    canvasAvailableWidth,
    fullTriggerRowWidthEstimate + paddingX * 2,
  );
  let contentWidth = mainLaneContentWidth;
  if (laneBranchCount > 0) {
    contentWidth = Math.max(mainLaneContentWidth, branchRowWidthEstimate + paddingX * 2);
  }

  let triggerRow = computeTriggerRowGeometry({
    triggerCount,
    triggerCardWidth,
    addTileWidth,
    gap,
    contentWidth: mainLaneContentWidth,
    includeAddTile,
  });

  if (fullTriggerRowWidthEstimate + paddingX * 2 > mainLaneContentWidth) {
    const expandedMainLaneWidth = fullTriggerRowWidthEstimate + paddingX * 2;
    triggerRow = computeTriggerRowGeometry({
      triggerCount,
      triggerCardWidth,
      addTileWidth,
      gap,
      contentWidth: expandedMainLaneWidth,
      includeAddTile,
    });
    contentWidth = Math.max(expandedMainLaneWidth, contentWidth);
  }

  const visibleRowCenterX = triggerRow.visibleRowCenterX;
  const actionLaneX = visibleRowCenterX;
  const nodes = [];
  let cursorY = paddingY;

  triggerRow.triggerSlots.forEach((slot, index) => {
    nodes.push(createWorkflowNode(
      `trigger-${index}`,
      'trigger',
      slot.x,
      cursorY,
      triggerCardWidth,
      triggerCardHeight,
      { label: longLabels && index === 0 ? 'Very long trigger label that should not distort connector geometry' : `Trigger ${index + 1}` },
    ));
  });

  let addTileNode = null;
  if (includeAddTile) {
    addTileNode = createWorkflowNode(
      'add-trigger',
      'addTrigger',
      triggerRow.addTileX,
      cursorY,
      addTileWidth,
      addTileHeight,
      { label: 'Add new trigger' },
    );
    nodes.push(addTileNode);
  }

  const triggerRowBottom = cursorY + triggerCardHeight;
  const mergeY = triggerRowBottom + triggerConnectorDrop;
  const triggerMergeGapFromCard = mergeY - triggerRowBottom;

  const triggerInsertPlusNode = createWorkflowNode(
    'trigger-insert-plus',
    'plus',
    actionLaneX - plusSize / 2,
    mergeY + trunkToPlusGap,
    plusSize,
    plusSize,
    { role: 'trigger-insert' },
  );
  nodes.push(triggerInsertPlusNode);

  cursorY = triggerInsertPlusNode.bottomY + plusToNextGap;

  const triggerNodes = nodes.filter((node) => node.type === 'trigger');
  const topRowItemNodes = [
    ...triggerNodes,
    ...(addTileNode ? [addTileNode] : []),
  ];
  const triggerRailLeftX = topRowItemNodes.length > 1
    ? triggerRow.triggerRowVisualCenterX - triggerCenterRadius
    : null;
  const triggerRailRightX = topRowItemNodes.length > 1
    ? triggerRow.triggerRowVisualCenterX + triggerCenterRadius
    : null;

  let actionNode = null;
  let mainPlusNode = null;
  let conditionNode = null;
  let mainEndNode = null;
  const actionNodes = [];
  const mainPlusNodes = [];
  const resolvedActionCount = flags.multiAction
    ? Math.max(1, actionCount ?? 3)
    : (flags.hasAction ? 1 : 0);

  if (resolvedActionCount > 0) {
    for (let actionIndex = 0; actionIndex < resolvedActionCount; actionIndex += 1) {
      const isSmsAction = actionIndex === 0;
      const node = createWorkflowNode(
        `action-${actionIndex}`,
        'action',
        actionLaneX - actionWidth / 2,
        cursorY,
        actionWidth,
        actionHeight,
        {
          label: isSmsAction ? 'Send SMS' : `Action ${actionIndex + 1}`,
          sublabel: isSmsAction ? 'sms' : 'action',
          selected: isSmsAction,
        },
      );
      actionNodes.push(node);
      nodes.push(node);
      actionNode = node;
      cursorY = node.bottomY + stepConnectorHeight;

      const shouldAddPlus = flags.hasFullFlowPlus
        && (actionIndex < resolvedActionCount - 1 || flags.hasCondition);
      if (shouldAddPlus) {
        const plusNode = createWorkflowNode(
          `main-plus-${actionIndex}`,
          'plus',
          actionLaneX - plusSize / 2,
          cursorY,
          plusSize,
          plusSize,
        );
        mainPlusNodes.push(plusNode);
        mainPlusNode = plusNode;
        nodes.push(plusNode);
        cursorY = plusNode.bottomY + stepConnectorHeight;
      }
    }
  } else if (flags.hasAction) {
    actionNode = createWorkflowNode(
      'action-sms',
      'action',
      actionLaneX - actionWidth / 2,
      cursorY,
      actionWidth,
      actionHeight,
      { label: 'Send SMS', sublabel: 'sms' },
    );
    nodes.push(actionNode);
    actionNodes.push(actionNode);
    cursorY = actionNode.bottomY + stepConnectorHeight;

    if (flags.hasFullFlowPlus) {
      mainPlusNode = createWorkflowNode(
        'main-plus',
        'plus',
        actionLaneX - plusSize / 2,
        cursorY,
        plusSize,
        plusSize,
      );
      mainPlusNodes.push(mainPlusNode);
      nodes.push(mainPlusNode);
      cursorY = mainPlusNode.bottomY + stepConnectorHeight;
    }
  }

  if (flags.hasCondition) {
    conditionNode = createWorkflowNode(
      'condition',
      'condition',
      actionLaneX - conditionWidth / 2,
      cursorY,
      conditionWidth,
      conditionHeight,
      { label: 'Condition', sublabel: 'if_else' },
    );
    nodes.push(conditionNode);
  }

  if (flags.hasMainEnd) {
    mainEndNode = createWorkflowNode(
      'main-end',
      'end',
      actionLaneX - endWidth / 2,
      cursorY,
      endWidth,
      endHeight,
      { label: 'End of Workflow' },
    );
    nodes.push(mainEndNode);
    cursorY = mainEndNode.bottomY;
  }

  let branchGeometry = null;
  const branchNodes = [];
  const branchPlusNodes = [];
  const branchEndNodes = [];

  if (flags.hasBranches && conditionNode) {
    // Branch row may widen scrollable content, but must never recompute trigger/main lane geometry.
    if (branchRowWidthEstimate + paddingX * 2 > contentWidth) {
      contentWidth = branchRowWidthEstimate + paddingX * 2;
    }

    branchGeometry = computeBranchRowGeometry({
      branchCount: laneBranchCount,
      branchCardWidth: branchWidth,
      branchGap,
      conditionCenterX: actionLaneX,
      contentWidth,
      paddingX,
    });

    const branchTopY = conditionNode.bottomY + forkDropHeight;
    const branchRailY = branchTopY - branchRailGapAboveCard;
    const branchRailGap = branchTopY - branchRailY;
    const branchLabels = laneBranchCount === 2
      ? ['Branch 1', 'None']
      : ['Branch 1', 'Branch 2', 'None'];

    branchGeometry.branchSlots.forEach((slot, index) => {
      const isNone = index === laneBranchCount - 1;
      const branchNode = createWorkflowNode(
        `branch-${index}`,
        'branch',
        slot.x,
        branchTopY,
        branchWidth,
        branchHeight,
        {
          label: longLabels && !isNone
            ? `${branchLabels[index]} with an intentionally long label for proof`
            : branchLabels[index],
          isNone,
        },
      );
      branchNodes.push(branchNode);
      nodes.push(branchNode);

      const plusNode = createWorkflowNode(
        `branch-plus-${index}`,
        'plus',
        slot.centerX - plusSize / 2,
        branchNode.bottomY + branchLaneDrop,
        plusSize,
        plusSize,
      );
      branchPlusNodes.push(plusNode);
      nodes.push(plusNode);

      const endNode = createWorkflowNode(
        `branch-end-${index}`,
        'end',
        slot.centerX - endWidth / 2,
        plusNode.bottomY + branchLaneDrop,
        endWidth,
        endHeight,
        { label: 'End', isNone },
      );
      branchEndNodes.push(endNode);
      nodes.push(endNode);

      cursorY = Math.max(cursorY, endNode.bottomY);
    });
  }

  const contentHeight = Math.max(canvasHeight - paddingY * 2, cursorY + paddingY);
  const usesHorizontalScroll = contentWidth > canvasAvailableWidth;

  const addTileRightX = addTileNode?.rightX ?? triggerRow.addTileX + addTileWidth;
  const addTileOverflow = includeAddTile
    ? addTileRightX > contentWidth - paddingX
    : false;
  const branchOverflow = branchGeometry
    ? branchGeometry.branchRowStartX + branchGeometry.branchRowWidth > contentWidth - paddingX
    : false;
  const cardOverflow = nodes.some(
    (node) => node.rightX > contentWidth - paddingX || node.leftX < paddingX,
  ) && !usesHorizontalScroll;

  const firstDownstreamNode = actionNode ?? conditionNode ?? mainEndNode;
  const plusButtonCenterX = triggerInsertPlusNode.centerX;
  const firstActionCenterX = firstDownstreamNode?.centerX ?? plusButtonCenterX;
  const graphRootWidth = canvasWidth;
  const visualViewportWidth = drawerOpen
    ? Math.max(canvasWidth - editorWidth, MIN_COMFORTABLE_CANVAS_WIDTH)
    : canvasWidth;

  const debug = {
    drawerOpen,
    graphRootWidth,
    visualViewportWidth,
    mainLaneX: actionLaneX,
    conditionParentCenterX: conditionNode?.centerX ?? null,
    branchRowWidth: branchGeometry?.branchRowWidth ?? null,
    didMainLaneMove: false,
    canvas: {
      width: canvasWidth,
      availableWidth: canvasAvailableWidth,
      paddingX,
    },
    content: {
      width: contentWidth,
      height: contentHeight,
      usesHorizontalScroll,
    },
    triggerRow: {
      triggerCount,
      triggerGroupWidth: triggerRow.triggerGroupWidth,
      fullTriggerRowWidth: triggerRow.fullTriggerRowWidth,
      rowStartX: triggerRow.rowStartX,
      triggerCenters: triggerRow.triggerCenters,
      addTileCenterX: triggerRow.addTileCenterX,
      itemCenters: triggerRow.itemCenters,
      visibleItemCenters: triggerRow.itemCenters,
      visibleRowCenterX,
      triggerRowVisualCenterX: triggerRow.triggerRowVisualCenterX,
      actionLaneCenterX: actionLaneX,
      triggerMergeCenterX: actionLaneX,
      addTileIncludedInMerge: includeAddTile && triggerCount > 0,
    },
    triggerBottomY: triggerRowBottom,
    addTileBottomY: addTileNode?.bottomY ?? null,
    stemDrop: triggerConnectorDrop,
    triggerConnectorDrop,
    mergeCenterX: actionLaneX,
    mergeY,
    triggerMergeY: mergeY,
    triggerMergeGapFromCard,
    centerRadius: triggerCenterRadius,
    triggerRailLeftX,
    triggerRailRightX,
    curveRadius: triggerCornerRadius,
    plusButtonCenterX,
    firstActionCenterX,
    deltaPlusFromMerge: Math.abs(plusButtonCenterX - actionLaneX),
    deltaActionFromMerge: firstDownstreamNode
      ? Math.abs(firstActionCenterX - actionLaneX)
      : 0,
    rowUsesScroll: usesHorizontalScroll,
    addTileOverflow,
    actionLane: {
      actionLaneX,
      deltaFromTriggerMerge: Math.abs(actionLaneX - triggerRow.triggerRowVisualCenterX),
    },
    condition: conditionNode
      ? {
        conditionCenterX: conditionNode.centerX,
        deltaFromActionLane: Math.abs(conditionNode.centerX - actionLaneX),
        branchConnectorY: branchNodes[0] ? branchNodes[0].topY - branchRailGapAboveCard : null,
        branchCardTopY: branchNodes[0]?.topY ?? null,
        conditionRailLeftX: branchNodes.length > 0
          ? conditionNode.centerX - conditionCenterRadius
          : null,
        conditionRailRightX: branchNodes.length > 0
          ? conditionNode.centerX + conditionCenterRadius
          : null,
      }
      : null,
    branches: branchGeometry
      ? {
        branchRowWidth: branchGeometry.branchRowWidth,
        branchCenters: branchGeometry.branchCenters,
        branchTopY: branchNodes[0]?.topY ?? null,
        branchRailY: branchNodes[0] ? branchNodes[0].topY - branchRailGapAboveCard : null,
        branchRailGap: branchRailGapAboveCard,
        plusCenters: branchPlusNodes.map((node) => node.centerX),
        endCenters: branchEndNodes.map((node) => node.centerX),
      }
      : null,
    overflow: {
      addTileOverflow,
      branchOverflow,
      cardOverflow,
    },
    aligned: Math.abs(actionLaneX - plusButtonCenterX) <= 1
      && Math.abs(actionLaneX - firstActionCenterX) <= 1
      && (!addTileOverflow || usesHorizontalScroll)
      && (!branchOverflow || usesHorizontalScroll)
      && !cardOverflow,
  };

  return {
    canvas: {
      width: canvasWidth,
      height: canvasHeight,
      paddingX,
      paddingY,
      overflow: 'hidden',
    },
    content: {
      width: contentWidth,
      height: contentHeight,
      originX: 0,
      originY: 0,
      usesHorizontalScroll,
    },
    nodes,
    triggerRow,
    visibleRowCenterX,
    actionLaneX,
    mergeY,
    triggerCornerRadius,
    triggerCenterRadius,
    branchRailY: branchNodes[0] ? branchNodes[0].topY - branchRailGapAboveCard : null,
    branchCornerRadius,
    conditionCenterRadius,
    topRowItemNodes,
    triggerInsertPlusNode,
    conditionNode,
    branchNodes,
    branchPlusNodes,
    branchEndNodes,
    addTileNode,
    triggerNodes,
    actionNode,
    mainPlusNode,
    mainEndNode,
    splitY: conditionNode ? conditionNode.bottomY + forkStemHeight : null,
    debug,
    scenario,
    branchCount: laneBranchCount,
  };
}
